import { UNGROUPED_RESOURCES_GROUP_ID } from '@/console/features/groups/components/group-list/group-list.constants';
import {
  api,
  VerifyTotpTwoFactorAuthDocument,
} from '@/shared/redux/api/_generated/api/ident-api.generated';
import { TRtkApiTags } from '@/shared/types/api.types';
import { VerifyTotpTwoFactorAuthMutation } from '@/shared/types/types.generated';

type TRbacApiTags = 'User' | 'UserRole';

export const identApi = api.enhanceEndpoints({
  addTagTypes: [
    'Tenant',
    'User',
    'UserRole',
    'Groups',
    'TermsAndConditions',
    'UserUiPreferences',
  ],
  endpoints: {
    ReadAllUsers: {
      providesTags: (result, _, args) => {
        const tags: TRtkApiTags<TRbacApiTags> = [{ type: 'User', id: 'LIST' }];

        if (result?.readAllUsers) {
          result?.readAllUsers.items.forEach((user) => {
            tags.push({ type: 'User', id: user.userId });
          });
        }

        const filteredGroup = args?.filter?.groupId;
        const groupId = filteredGroup?.contains ?? UNGROUPED_RESOURCES_GROUP_ID;
        if (filteredGroup) {
          return [...tags, { type: 'Groups' as const, id: groupId }];
        }

        return tags;
      },
    },
    // Name-only lookups (user filter, session history). Shares the list tag
    // with ReadAllUsers, so whatever refreshes the users list (the user
    // mutations below and the ident AppSync events) refreshes these too.
    ReadAllUsersBase: {
      providesTags: [{ type: 'User', id: 'LIST' }],
    },
    ReadUsersForSelect: {
      providesTags: (result) => {
        const tags: TRtkApiTags<TRbacApiTags> = [{ type: 'User', id: 'LIST' }];

        result?.readAllUsers.items.forEach((user) => {
          tags.push({ type: 'User', id: user.userId });
        });

        return tags;
      },
    },
    ReadRole: {
      providesTags: (result, _, __) => [
        { type: 'UserRole', id: result?.readUserRole?.userRoleId },
      ],
    },
    ReadAllRoles: {
      providesTags: (result, _, args) => {
        const tags: TRtkApiTags<TRbacApiTags> = [
          { type: 'UserRole', id: 'LIST' },
        ];

        if (result?.readAllUserRoles) {
          result?.readAllUserRoles.items.forEach((userRole) => {
            tags.push({ type: 'UserRole', id: userRole?.userRoleId });
          });
        }

        const filteredGroup = args?.filter?.groupId;
        const groupId = filteredGroup?.contains ?? UNGROUPED_RESOURCES_GROUP_ID;
        if (filteredGroup) {
          return [...tags, { type: 'Groups' as const, id: groupId }];
        }

        return tags;
      },
    },
    ReadRolesForSelect: {
      providesTags: (result) => {
        const tags: TRtkApiTags<TRbacApiTags> = [
          { type: 'UserRole', id: 'LIST' },
        ];

        result?.readAllUserRoles.items.forEach((userRole) => {
          tags.push({ type: 'UserRole', id: userRole.userRoleId });
        });

        return tags;
      },
    },
    ReadUser: {
      providesTags: (_, __, arg) => [{ type: 'User', id: arg.userId }],
    },
    DeleteUser: {
      invalidatesTags: [{ type: 'User', id: 'LIST' }, 'Groups' as const],
    },
    LinkRoleToUser: {
      invalidatesTags: (_, __, arg) => [
        { type: 'User', id: arg.userId },
        { type: 'UserRole', id: arg?.userRoleId },
        { type: 'User', id: 'LIST' },
      ],
    },
    CreateUserRole: {
      invalidatesTags: [{ type: 'UserRole', id: 'LIST' }, 'Groups' as const],
    },
    DeleteRole: {
      invalidatesTags: (_, __, arg) => [
        { type: 'UserRole' as const, id: arg?.userRoleId },
        { type: 'UserRole' as const, id: 'LIST' },
        // Deleting a role drops it from every user row that held it.
        { type: 'User' as const, id: 'LIST' },
        'Groups' as const,
      ],
    },
    RemoveRoleFromUser: {
      invalidatesTags: (_, __, arg) => [
        { type: 'User', id: arg.userId },
        { type: 'UserRole', id: arg?.userRoleId },
        { type: 'UserRole', id: 'LIST' },
      ],
    },
    UpdateUser: {
      invalidatesTags: (_, __, arg) => [
        { type: 'User' as const, id: arg.userId },
        { type: 'User' as const, id: 'LIST' },
        'Groups' as const,
      ],
    },
    UpdateUserRole: {
      invalidatesTags: (_, __, arg) => [
        { type: 'UserRole' as const, id: arg?.userRoleId },
        { type: 'UserRole' as const, id: 'LIST' },
        // Every user row embeds the roles it holds.
        { type: 'User' as const, id: 'LIST' },
        'Groups' as const,
      ],
    },
    ReadUsersForRole: {
      providesTags: (result, _, arg) => {
        const tags: TRtkApiTags<TRbacApiTags> = [
          { type: 'UserRole', id: arg?.userRoleId },
        ];

        if (result?.readUsersForUserRole) {
          result?.readUsersForUserRole.items.forEach((user) => {
            tags.push({ type: 'User', id: user.userId });
          });
        }

        return tags;
      },
    },
    ReadUserRoleLinksForUser: {
      providesTags: (_, __, arg) => [{ type: 'User', id: arg.userId }],
    },
    ReadUserRoleLinksForRole: {
      providesTags: (_, __, arg) => [{ type: 'UserRole', id: arg?.userRoleId }],
    },
    UpdateUserRoleLink: {
      invalidatesTags: (_, __, arg) => [
        { type: 'User', id: arg.userId },
        { type: 'UserRole', id: arg?.userRoleId },
      ],
    },
    ReadUsersCount: {
      providesTags: [{ type: 'User', id: 'LIST' }],
    },
    ReadGroupedIdentityCounts: {
      providesTags: (result) =>
        (result?.readGroupedIdentityCounts ?? []).map((count) => ({
          type: 'Groups' as const,
          id: count.groupId,
        })),
    },
    ReadGroupedIdentitiesCount: {
      providesTags: (_, __, arg) => [{ type: 'Groups', id: arg.groupId }],
    },
    ReadUngroupedIdentitiesCount: {
      providesTags: [{ type: 'Groups', id: UNGROUPED_RESOURCES_GROUP_ID }],
    },
    ReadPoliciesForOwnUser: {
      providesTags: (result) => [
        { type: 'UserRole' as const, id: 'LIST' },
        ...(result?.readUserRolesForOwnUser?.items ?? []).map((userRole) => ({
          type: 'UserRole' as const,
          id: userRole.userRoleId,
        })),
      ],
    },
    ReadTenant: {
      providesTags: ['Tenant'],
    },
    UpdateTenant: {
      invalidatesTags: ['Tenant'],
    },
    ReadTermsAndConditionsAcceptance: {
      providesTags: ['TermsAndConditions'],
    },
    AcceptTermsAndConditions: {
      invalidatesTags: ['TermsAndConditions'],
    },
    ReadUserUiPreferences: {
      providesTags: ['UserUiPreferences'],
    },
    UpdateUserUiPreferences: {
      invalidatesTags: ['UserUiPreferences'],
    },
  },
});

type VerifyTotpTwoFactorAuthArgs = {
  challenge: string;
  extraHeaders?: Record<string, string>;
};

const identApiWithOverrides = identApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    VerifyTotpTwoFactorAuth: build.mutation<
      VerifyTotpTwoFactorAuthMutation,
      VerifyTotpTwoFactorAuthArgs
    >({
      query: ({ extraHeaders, ...variables }) => ({
        document: VerifyTotpTwoFactorAuthDocument,
        variables,
        extraHeaders,
      }),
    }),
  }),
});

export const {
  useReadTenantQuery,
  useReadAllUsersBaseQuery,
  useReadAllUsersQuery,
  useReadUsersForSelectQuery,
  useReadAllRolesQuery,
  useReadRolesForSelectQuery,
  useCreateUserMutation,
  useLinkRoleToUserMutation,
  useCreateUserRoleMutation,
  useDeleteRoleMutation,
  useRemoveRoleFromUserMutation,
  useDeleteUserMutation,
  useReadUserQuery,
  useUpdateUserMutation,
  useReadRoleQuery,
  useUpdateUserRoleMutation,
  useReadUsersForRoleQuery,
  useReadTenantsForUserQuery,
  useSelectTenantMutation,
  useUpdateTenantMutation,
  useReadUserRoleLinksForUserQuery,
  useReadUserRoleLinksForRoleQuery,
  useUpdateUserRoleLinkMutation,
  useReadPoliciesForUserQuery,
  useReadPoliciesForOwnUserQuery,
  useReadUsersCountQuery,
  useReadGroupedIdentitiesCountQuery,
  useReadGroupedIdentityCountsQuery,
  useReadUngroupedIdentitiesCountQuery,
  useGenerateTwoFactorAuthTokenMutation,
  useAuthenticateTwoFactorAuthTokenMutation,
  useVerifyTotpTwoFactorAuthMutation,
  useReadTermsAndConditionsAcceptanceQuery,
  useAcceptTermsAndConditionsMutation,
  useReadUserUiPreferencesQuery,
  useUpdateUserUiPreferencesMutation,
} = identApiWithOverrides;
